<template>
  <SearchBox
    label="Search"
    prepend-inner-icon="mdi-magnify"
    hide-details="auto"
    variant="outlined"
    density="comfortable"
    style="max-width: 400px"
    v-model="search"
    @filter-applied="applyFilter()"
    :filters-applied="filtersApplied"
  >
    <template #filters v-if="localFilters">
      <div class="filter-line-item" v-if="currentUser?.hasPermission('clients.view_client')">
        <label>Clients</label>
        <div class="filter-line-item-value">
          <ClientAutoCompletePicker
            clearable
            :readonly="localFilters.client != null"
            v-model="localFilters.client"
            @update:model-value="setSiteFilterValue()"
            label=""
          />
        </div>
      </div>
      <div class="filter-line-item" v-if="currentUser?.hasPermission('clients.view_site')">
        <label>Site</label>
        <div class="filter-line-item-value">
          <SiteAutoCompletePicker
            clearable
            :readonly="localFilters.site != null"
            v-model="localFilters.site"
            :disabled="!localFilters.client"
            :client="localFilters.client"
            label=""
          />
        </div>
      </div>
      <div class="filter-line-item" v-if="currentUser?.hasPermission('employees.view_employee')">
        <label>Employee</label>
        <div class="filter-line-item-value">
          <EmployeeAutoCompletePicker
            clearable
            :readonly="localFilters.created_by != null"
            v-model="localFilters.created_by"
            label=""
          />
        </div>
      </div>
      <div class="filter-line-item">
        <label>State</label>
        <div class="filter-line-item-value">
          <ChitStateSelect clearable v-model="localFilters.state" label="" />
        </div>
      </div>

      <div class="filter-line-item">
        <label>Received After</label>
        <div class="filter-line-item-value">
          <DatePickerField clearable v-model="localFilters.received_at_after" />
        </div>
      </div>

      <div class="filter-line-item">
        <label>Received Before</label>
        <div class="filter-line-item-value">
          <DatePickerField clearable v-model="localFilters.received_at_before" />
        </div>
      </div>

      <div class="filter-line-item">
        <label>Arrived After</label>
        <div class="filter-line-item-value">
          <DatePickerField clearable v-model="localFilters.arrived_at_after" />
        </div>
      </div>

      <div class="filter-line-item">
        <label>Arrived Before</label>
        <div class="filter-line-item-value">
          <DatePickerField clearable v-model="localFilters.arrived_at_before" />
        </div>
      </div>

      <div class="filter-line-item">
        <label>Departed After</label>
        <div class="filter-line-item-value">
          <DatePickerField clearable v-model="localFilters.departed_at_after" />
        </div>
      </div>

      <div class="filter-line-item">
        <label>Departed Before</label>
        <div class="filter-line-item-value">
          <DatePickerField clearable v-model="localFilters.departed_at_before" />
        </div>
      </div>

      <div class="filter-line-item">
        <label>Created After</label>
        <div class="filter-line-item-value">
          <DatePickerField clearable v-model="localFilters.created_after" />
        </div>
      </div>

      <div class="filter-line-item">
        <label>Created Before</label>
        <div class="filter-line-item-value">
          <DatePickerField clearable v-model="localFilters.created_before" />
        </div>
      </div>

      <div class="filter-line-item">
        <label>Modified After</label>
        <div class="filter-line-item-value">
          <DatePickerField clearable v-model="localFilters.modified_after" />
        </div>
      </div>
    </template>
  </SearchBox>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'

import type { IChitFilterParam } from '@/services'
import { useAuthStore } from '@/stores/auth'
import { cloneDeep, omit } from 'lodash'

import ChitStateSelect from './ChitStateSelect.vue'
import DatePickerField from '@/components/common/DatePickerField.vue'
import SiteAutoCompletePicker from '@/components/sites/SiteAutoCompletePicker.vue'
import EmployeeAutoCompletePicker from '@/components/employees/EmployeeAutoCompletePicker.vue'
import ClientAutoCompletePicker from '@/components/clients/ClientAutoCompletePicker.vue'
import SearchBox from '@/components/common/SearchBox.vue'

const search = defineModel<string>('search')

interface Props {
  filters: IChitFilterParam
}
interface Emits {
  (e: 'filter-applied', filter: IChitFilterParam): void
}
const emit = defineEmits<Emits>()
const props = defineProps<Props>()

const authStore = useAuthStore()
const { getUser: currentUser } = storeToRefs(authStore)

const localFilters = ref<IChitFilterParam>({})

const filtersApplied = computed(() => omit(props.filters, ['page', 'search']))

watch(
  () => props.filters,
  (value) => {
    // do not assign reference value to localFilter
    localFilters.value = cloneDeep(value)
  },
  {
    immediate: true,
    deep: true
  }
)

function setSiteFilterValue() {
  if (!localFilters.value.client) {
    localFilters.value.site = null
  }
}

function applyFilter() {
  const updatedFilter = props.filters
  for (const filter in props.filters) {
    const filterKey = filter as keyof IChitFilterParam

    const updatedValue = localFilters.value[filterKey]
    // filter fields that are not null and that match the filter object
    if (updatedValue !== null) {
      updatedFilter[filterKey] = updatedValue as any
    } else {
      // reset other fields that are not set
      updatedFilter[filterKey] = undefined
    }
  }
  emit('filter-applied', updatedFilter)
}
</script>
